import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import logoImage from '../static/logo.jpg';

const sessionOneId = '6a63448bda9f6e0013d20aa5';
const sessionTwoId = '6a634647da9f6e0013d2144a';
const sessionIds = [sessionOneId, sessionTwoId];
const adminTokenStorageKey = 'hydrogenCouncilAdminToken';

const sessionLabels = {
  [sessionOneId]: 'Session 1',
  [sessionTwoId]: 'Session 2',
  sessionOne: 'Session 1',
  sessionTwo: 'Session 2',
};

const statusLabels = {
  interested: 'Interested',
  registering: 'Registering',
  registered: 'Registered',
  failed: 'Failed',
  needsReview: 'Needs review',
  rejected: 'Rejected',
};

const filterColumns = [
  { key: 'createdAt', label: 'Submitted At' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'jobTitle', label: 'Job title' },
  { key: 'company', label: 'Company' },
  {
    key: 'session',
    label: 'Session',
    options: ['Session 1', 'Session 2'],
  },
  {
    key: 'registrationStatus',
    label: 'Registration Status',
    options: Object.values(statusLabels),
  },
];

const tableColumns = [
  ...filterColumns,
  { key: 'action', label: 'Action' },
];

const buttonStyles = {
  background: '#25a5d8',
  color: 'white',
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 600,
  minHeight: 40,
  '&:hover': { background: '#707173' },
  '&.Mui-disabled': {
    background: '#d8dee0',
    color: '#6e777a',
  },
};

const outlinedButtonStyles = {
  borderColor: '#25a5d8',
  color: '#167fa8',
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 600,
  minHeight: 40,
  '&:hover': {
    borderColor: '#707173',
    background: '#f3f6f7',
  },
};

function formatDateTime(value) {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function normaliseValue(value) {
  return String(value || '').toLowerCase();
}

function getSessionLabel(registrant) {
  return sessionLabels[registrant.eventID] || sessionLabels[registrant.attending] || 'Unknown session';
}

function getRegistrationStatus(registrant) {
  return registrant.registrationStatus || 'interested';
}

function getRowDisplayValues(registrant) {
  return {
    createdAt: formatDateTime(registrant.createdAt),
    name: registrant.name || '',
    email: registrant.email || '',
    jobTitle: registrant.jobTitle || '',
    company: registrant.company || '',
    session: getSessionLabel(registrant),
    registrationStatus: statusLabels[getRegistrationStatus(registrant)] || 'Interested',
  };
}

function getStatusChipStyles(status) {
  if (status === 'registered') return { background: '#e4f4ec', color: '#17643b' };
  if (status === 'registering') return { background: '#e6f5fb', color: '#116c8f' };
  if (status === 'failed') return { background: '#fceaea', color: '#9a3030' };
  if (status === 'needsReview') return { background: '#fff1d6', color: '#835500' };
  if (status === 'rejected') return { background: '#f4e7e7', color: '#823131' };
  return { background: '#edf0f1', color: '#4f5b5f' };
}

function mergeRegistrants(resultSets) {
  const merged = new Map();

  resultSets.flat().forEach((registrant) => {
    merged.set(registrant._id, registrant);
  });

  return Array.from(merged.values()).sort((a, b) => (
    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  ));
}

function csvEscape(value) {
  const stringValue = String(value || '');
  return `"${stringValue.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function AdminPage() {
  const [password, setPassword] = useState('');
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem(adminTokenStorageKey) || '');
  const [registrants, setRegistrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [registeringId, setRegisteringId] = useState('');
  const [rejectingId, setRejectingId] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('registrations');
  const [globalSearch, setGlobalSearch] = useState('');
  const [filters, setFilters] = useState(
    filterColumns.reduce((acc, column) => ({ ...acc, [column.key]: '' }), {})
  );
  const [selectedFilterField, setSelectedFilterField] = useState('name');
  const [filterDraft, setFilterDraft] = useState('');
  const [selectedRegistrant, setSelectedRegistrant] = useState(null);
  const [selectedRejectRegistrant, setSelectedRejectRegistrant] = useState(null);

  const isLoggedIn = Boolean(adminToken);
  const selectedFilterColumn = filterColumns.find((column) => column.key === selectedFilterField);
  const registrationRegistrants = useMemo(() => (
    registrants.filter((registrant) => getRegistrationStatus(registrant) !== 'rejected')
  ), [registrants]);
  const rejectedRegistrants = useMemo(() => (
    registrants.filter((registrant) => getRegistrationStatus(registrant) === 'rejected')
  ), [registrants]);
  const tabRegistrants = activeTab === 'rejected' ? rejectedRegistrants : registrationRegistrants;

  const filteredRegistrants = useMemo(() => {
    const searchTerm = normaliseValue(globalSearch);

    return tabRegistrants.filter((registrant) => {
      const row = getRowDisplayValues(registrant);
      const matchesGlobalSearch = !searchTerm || Object.values(row).some((value) => (
        normaliseValue(value).includes(searchTerm)
      ));
      const matchesColumnFilters = filterColumns.every((column) => {
        const filterValue = normaliseValue(filters[column.key]);
        return !filterValue || normaliseValue(row[column.key]).includes(filterValue);
      });

      return matchesGlobalSearch && matchesColumnFilters;
    });
  }, [filters, globalSearch, tabRegistrants]);

  const activeFilterCount = useMemo(() => (
    Object.values(filters).filter(Boolean).length + (globalSearch ? 1 : 0)
  ), [filters, globalSearch]);

  const totals = useMemo(() => ({
    all: registrationRegistrants.length,
    sessionOne: registrationRegistrants.filter((registrant) => registrant.eventID === sessionOneId).length,
    sessionTwo: registrationRegistrants.filter((registrant) => registrant.eventID === sessionTwoId).length,
    registered: registrationRegistrants.filter((registrant) => getRegistrationStatus(registrant) === 'registered').length,
    rejected: rejectedRegistrants.length,
  }), [registrationRegistrants, rejectedRegistrants]);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(adminTokenStorageKey);
    setAdminToken('');
    setRegistrants([]);
  }, []);

  async function handleLogin(event) {
    event.preventDefault();
    setError('');
    setLoginLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/registrant/admin/login/?token=${process.env.REACT_APP_API_BASE_TOKEN}`,
        { password, client: 'hydrogenCouncil' }
      );

      if (!response.data.success || !response.data.token) {
        setError(response.data.message || 'Unable to log in');
        return;
      }

      sessionStorage.setItem(adminTokenStorageKey, response.data.token);
      setAdminToken(response.data.token);
      setPassword('');
    } catch (loginError) {
      setError(loginError.response?.data?.message || 'Unable to log in');
    } finally {
      setLoginLoading(false);
    }
  }

  const fetchRegistrants = useCallback(async (token) => {
    setError('');
    setLoading(true);

    try {
      const responses = await Promise.all(sessionIds.map((eventID) => (
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/registrant/admin/inPersonRegistrants/?token=${process.env.REACT_APP_API_BASE_TOKEN}&eventID=${eventID}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
      )));

      const failedResponse = responses.find((response) => !response.data.success);

      if (failedResponse) {
        setError(failedResponse.data.message || 'Unable to load registrations');
        return;
      }

      setRegistrants(mergeRegistrants(
        responses.map((response) => response.data.registrants || [])
      ));
    } catch (fetchError) {
      if (fetchError.response?.status === 401) {
        handleLogout();
        setError('Admin session expired. Please log in again.');
      } else {
        setError(fetchError.response?.data?.message || 'Unable to load registrations');
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    if (!adminToken) return;

    fetchRegistrants(adminToken);
  }, [adminToken, fetchRegistrants]);

  function handleFilterChange(key, value) {
    setFilters((previousFilters) => ({
      ...previousFilters,
      [key]: value,
    }));
  }

  function addFilter() {
    if (!filterDraft.trim()) return;

    handleFilterChange(selectedFilterField, filterDraft.trim());
    setFilterDraft('');
  }

  function removeFilter(key) {
    handleFilterChange(key, '');
  }

  function clearFilters() {
    setGlobalSearch('');
    setFilters(filterColumns.reduce((acc, column) => ({ ...acc, [column.key]: '' }), {}));
    setFilterDraft('');
  }

  function updateRegistrant(updatedRegistrant) {
    if (!updatedRegistrant?._id) return;

    setRegistrants((currentRegistrants) => currentRegistrants.map((registrant) => (
      registrant._id === updatedRegistrant._id ? { ...registrant, ...updatedRegistrant } : registrant
    )));
  }

  async function handleRegisterRegistrant() {
    if (!selectedRegistrant || registeringId) return;

    setError('');
    setRegisteringId(selectedRegistrant._id);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/registrant/admin/inPersonRegistrants/${selectedRegistrant._id}/register/?token=${process.env.REACT_APP_API_BASE_TOKEN}`,
        {
          eventid: selectedRegistrant.eventID,
          fields: {
            name: selectedRegistrant.name,
            company: selectedRegistrant.company,
            email: selectedRegistrant.email,
            jobTitle: selectedRegistrant.jobTitle,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );

      if (!response.data.success) {
        updateRegistrant(response.data.registrant);
        setError(response.data.message || 'Unable to register attendee');
        return;
      }

      updateRegistrant(response.data.registrant);
      setSelectedRegistrant(null);
      setSuccessMessage(`${selectedRegistrant.name} has been registered for the ${getSessionLabel(selectedRegistrant)}.`);
    } catch (registrationError) {
      if (registrationError.response?.status === 401) {
        setSelectedRegistrant(null);
        handleLogout();
        setError('Admin session expired. Please log in again.');
      } else {
        updateRegistrant(registrationError.response?.data?.registrant);
        setSelectedRegistrant(null);
        setError(registrationError.response?.data?.message || 'Unable to register attendee');
      }
    } finally {
      setRegisteringId('');
    }
  }

  async function handleRejectRegistrant() {
    if (!selectedRejectRegistrant || rejectingId) return;

    setError('');
    setRejectingId(selectedRejectRegistrant._id);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/registrant/admin/inPersonRegistrants/${selectedRejectRegistrant._id}/reject/?token=${process.env.REACT_APP_API_BASE_TOKEN}`,
        { eventid: selectedRejectRegistrant.eventID },
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );

      if (!response.data.success) {
        updateRegistrant(response.data.registrant);
        setError(response.data.message || 'Unable to reject registration');
        return;
      }

      updateRegistrant(response.data.registrant);
      setSelectedRejectRegistrant(null);
      setSuccessMessage(`${selectedRejectRegistrant.name} has been moved to rejected.`);
    } catch (rejectError) {
      if (rejectError.response?.status === 401) {
        setSelectedRejectRegistrant(null);
        handleLogout();
        setError('Admin session expired. Please log in again.');
      } else {
        updateRegistrant(rejectError.response?.data?.registrant);
        setSelectedRejectRegistrant(null);
        setError(rejectError.response?.data?.message || 'Unable to reject registration');
      }
    } finally {
      setRejectingId('');
    }
  }

  function handleExportRegistrations() {
    const rows = [
      ['Submitted At', 'Name', 'Email', 'Job title', 'Company', 'Session', 'Registration Status'],
      ...filteredRegistrants.map((registrant) => {
        const row = getRowDisplayValues(registrant);

        return [
          row.createdAt,
          row.name,
          row.email,
          row.jobTitle,
          row.company,
          row.session,
          row.registrationStatus,
        ];
      }),
    ];

    downloadCsv('hydrogen-council-registrations.csv', rows);
  }

  function renderRegistrationAction(registrant, fullWidth = false) {
    const status = getRegistrationStatus(registrant);
    const isRegistering = status === 'registering' || registeringId === registrant._id;
    const isRejecting = rejectingId === registrant._id;

    if (status === 'rejected') {
      return (
        <Button
          disabled
          startIcon={<DeleteOutlineIcon />}
          sx={{ ...outlinedButtonStyles, width: fullWidth ? '100%' : 'auto' }}
        >
          Rejected
        </Button>
      );
    }

    if (status === 'registered') {
      return (
        <Button
          disabled
          startIcon={<CheckCircleOutlineIcon />}
          sx={{ ...buttonStyles, width: fullWidth ? '100%' : 'auto' }}
        >
          Registered
        </Button>
      );
    }

    if (status === 'needsReview') {
      return (
        <Stack direction={fullWidth ? 'column' : 'row'} spacing={1}>
          <Button
            disabled
            startIcon={<WarningAmberIcon />}
            sx={{ ...buttonStyles, width: fullWidth ? '100%' : 'auto' }}
          >
            Review
          </Button>
          <Button
            onClick={() => setSelectedRejectRegistrant(registrant)}
            disabled={isRejecting}
            variant="outlined"
            startIcon={isRejecting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{ ...outlinedButtonStyles, width: fullWidth ? '100%' : 'auto', whiteSpace: 'nowrap' }}
          >
            {isRejecting ? 'Rejecting...' : 'Reject'}
          </Button>
        </Stack>
      );
    }

    return (
      <Stack direction={fullWidth ? 'column' : 'row'} spacing={1}>
        <Button
          onClick={() => setSelectedRegistrant(registrant)}
          disabled={isRegistering || isRejecting}
          startIcon={isRegistering ? <CircularProgress size={16} color="inherit" /> : <PersonAddAlt1Icon />}
          sx={{ ...buttonStyles, width: fullWidth ? '100%' : 'auto', whiteSpace: 'nowrap' }}
        >
          {isRegistering ? 'Registering...' : status === 'failed' ? 'Retry' : 'Register'}
        </Button>
        <Button
          onClick={() => setSelectedRejectRegistrant(registrant)}
          disabled={isRegistering || isRejecting}
          variant="outlined"
          startIcon={isRejecting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          sx={{ ...outlinedButtonStyles, width: fullWidth ? '100%' : 'auto', whiteSpace: 'nowrap' }}
        >
          {isRejecting ? 'Rejecting...' : 'Reject'}
        </Button>
      </Stack>
    );
  }

  const summaryItems = [
    { label: 'Total interest', value: totals.all },
    { label: 'Session 1 interest', value: totals.sessionOne },
    { label: 'Session 2 interest', value: totals.sessionTwo },
    { label: 'Registered', value: totals.registered },
    { label: 'Rejected', value: totals.rejected },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: '#f2f4f5', color: '#343a3c' }}>
      <CssBaseline />
      <Box sx={{ maxWidth: 1320, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 2, md: 4 } }}>
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
              pb: 2,
              borderBottom: '1px solid #d8dee0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2.5 }, minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: 150, sm: 205 },
                  height: { xs: 64, sm: 78 },
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  background: 'white',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={logoImage}
                  alt="Hydrogen Council"
                  style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Box>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
                <Typography component="h1" sx={{ fontSize: { sm: '1.45rem', md: '1.7rem' }, fontWeight: 700, lineHeight: 1.2 }}>
                  Hydrogen Council Registration Admin
                </Typography>
                <Typography sx={{ color: '#657074', mt: 0.5 }}>
                  Hydrogen Council 2026 Compass Report CEO Roundtable
                </Typography>
              </Box>
            </Box>

            {isLoggedIn && (
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Button
                  onClick={() => fetchRegistrants(adminToken)}
                  disabled={loading}
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  sx={{ ...outlinedButtonStyles, flex: { xs: 1, md: 'none' } }}
                >
                  Refresh
                </Button>
                <Button
                  onClick={handleLogout}
                  variant="outlined"
                  startIcon={<LogoutIcon />}
                  sx={{ ...outlinedButtonStyles, flex: { xs: 1, md: 'none' } }}
                >
                  Logout
                </Button>
              </Stack>
            )}
          </Box>

          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Typography component="h1" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
              Hydrogen Council Registration Admin
            </Typography>
            <Typography sx={{ color: '#657074', fontSize: '0.9rem', mt: 0.5 }}>
              2026 Compass Report CEO Roundtable
            </Typography>
          </Box>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          {!isLoggedIn ? (
            <Paper
              elevation={0}
              sx={{
                maxWidth: 440,
                width: '100%',
                p: { xs: 2, sm: 3 },
                borderRadius: '6px',
                border: '1px solid #d8dee0',
              }}
            >
              <Stack component="form" spacing={2} onSubmit={handleLogin}>
                <Box>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                    Admin login
                  </Typography>
                  <Typography sx={{ color: '#657074', fontSize: '0.9rem', mt: 0.5 }}>
                    Enter the event administration password.
                  </Typography>
                </Box>
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  fullWidth
                  autoComplete="current-password"
                />
                <Button type="submit" disabled={loginLoading} sx={buttonStyles}>
                  {loginLoading ? 'Logging in...' : 'Login'}
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Stack spacing={2.5}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {summaryItems.map((item) => (
                  <Paper
                    key={item.label}
                    elevation={0}
                    sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: '6px', border: '1px solid #d8dee0' }}
                  >
                    <Typography sx={{ color: '#657074', fontSize: '0.82rem' }}>{item.label}</Typography>
                    <Typography sx={{ fontSize: { xs: '1.55rem', sm: '1.8rem' }, fontWeight: 700 }}>{item.value}</Typography>
                  </Paper>
                ))}
              </Box>

              <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: '6px', border: '1px solid #d8dee0' }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box>
                      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
                        Search and filters
                      </Typography>
                      <Typography sx={{ color: '#657074', fontSize: '0.88rem' }}>
                        Search attendee details or narrow the results by one or more fields.
                      </Typography>
                    </Box>
                    {activeFilterCount > 0 && (
                      <Button onClick={clearFilters} variant="outlined" sx={outlinedButtonStyles}>
                        Clear filters
                      </Button>
                    )}
                  </Box>

                  <TextField
                    placeholder="Search all registration fields"
                    value={globalSearch}
                    onChange={(event) => setGlobalSearch(event.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#657074' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '220px minmax(240px, 1fr) auto' },
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      select
                      label="Filter field"
                      value={selectedFilterField}
                      onChange={(event) => {
                        setSelectedFilterField(event.target.value);
                        setFilterDraft('');
                      }}
                      size="small"
                    >
                      {filterColumns.map((column) => (
                        <MenuItem key={column.key} value={column.key}>
                          {column.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    {selectedFilterColumn?.options ? (
                      <TextField
                        select
                        label={`Filter ${selectedFilterColumn.label}`}
                        value={filterDraft}
                        onChange={(event) => setFilterDraft(event.target.value)}
                        size="small"
                      >
                        {selectedFilterColumn.options.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField
                        label={`Filter ${selectedFilterColumn?.label || 'field'}`}
                        value={filterDraft}
                        onChange={(event) => setFilterDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            addFilter();
                          }
                        }}
                        size="small"
                      />
                    )}

                    <Button onClick={addFilter} disabled={!filterDraft.trim()} sx={{ ...buttonStyles, minWidth: 112 }}>
                      Add filter
                    </Button>
                  </Box>

                  {Object.values(filters).some(Boolean) && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {filterColumns.map((column) => (
                        filters[column.key] ? (
                          <Chip
                            key={`${column.key}-chip`}
                            label={`${column.label}: ${filters[column.key]}`}
                            onDelete={() => removeFilter(column.key)}
                            sx={{ background: '#e6f5fb', color: '#116c8f', fontWeight: 600 }}
                          />
                        ) : null
                      ))}
                    </Box>
                  )}
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ borderRadius: '6px', border: '1px solid #d8dee0', overflow: 'hidden' }}>
                <Tabs
                  value={activeTab}
                  onChange={(event, nextTab) => setActiveTab(nextTab)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 48,
                    '& .MuiTab-root': {
                      minHeight: 48,
                      textTransform: 'none',
                      fontWeight: 700,
                      color: '#657074',
                    },
                    '& .Mui-selected': { color: '#167fa8' },
                    '& .MuiTabs-indicator': { backgroundColor: '#25a5d8' },
                  }}
                >
                  <Tab value="registrations" label={`Registrations (${registrationRegistrants.length})`} />
                  <Tab value="rejected" label={`Rejected (${rejectedRegistrants.length})`} />
                </Tabs>
              </Paper>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Box>
                  <Typography component="h2" sx={{ fontSize: '1.15rem', fontWeight: 700 }}>
                    {activeTab === 'rejected' ? 'Rejected registrations' : 'Registrations'}
                  </Typography>
                  <Typography sx={{ color: '#657074', fontSize: '0.88rem' }}>
                    Showing {filteredRegistrants.length} of {tabRegistrants.length}
                  </Typography>
                </Box>
                {activeTab === 'registrations' && (
                  <Button
                    onClick={handleExportRegistrations}
                    disabled={filteredRegistrants.length === 0}
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    sx={{ ...outlinedButtonStyles, alignSelf: { xs: 'stretch', sm: 'center' } }}
                  >
                    Export CSV
                  </Button>
                )}
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#25a5d8' }} />
                </Box>
              ) : (
                <>
                  <Paper elevation={0} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: '6px', border: '1px solid #d8dee0', overflow: 'hidden' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" stickyHeader sx={{ minWidth: 1080 }}>
                        <TableHead>
                          <TableRow>
                            {tableColumns.map((column) => (
                              <TableCell
                                key={column.key}
                                sx={{
                                  fontWeight: 700,
                                  color: '#343a3c',
                                  minWidth: column.key === 'action' ? 135 : 145,
                                  background: '#f7f9f9',
                                }}
                              >
                                {column.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredRegistrants.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 5, color: '#657074' }}>
                                No registrations found.
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredRegistrants.map((registrant) => {
                              const row = getRowDisplayValues(registrant);
                              const status = getRegistrationStatus(registrant);

                              return (
                                <TableRow key={registrant._id} hover>
                                  <TableCell>{row.createdAt}</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                                  <TableCell sx={{ wordBreak: 'break-word' }}>{row.email}</TableCell>
                                  <TableCell>{row.jobTitle || '-'}</TableCell>
                                  <TableCell>{row.company || '-'}</TableCell>
                                  <TableCell>{row.session}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={row.registrationStatus}
                                      size="small"
                                      sx={{ ...getStatusChipStyles(status), fontWeight: 600 }}
                                    />
                                  </TableCell>
                                  <TableCell>{renderRegistrationAction(registrant)}</TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
                    {filteredRegistrants.length === 0 ? (
                      <Box sx={{ py: 5, textAlign: 'center', color: '#657074' }}>
                        No registrations found.
                      </Box>
                    ) : (
                      filteredRegistrants.map((registrant) => {
                        const row = getRowDisplayValues(registrant);
                        const status = getRegistrationStatus(registrant);

                        return (
                          <Paper
                            key={registrant._id}
                            elevation={0}
                            sx={{ p: 2, borderRadius: '6px', border: '1px solid #d8dee0' }}
                          >
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>{row.name || 'Unnamed'}</Typography>
                                  <Typography sx={{ color: '#657074', fontSize: '0.88rem', wordBreak: 'break-word' }}>{row.email}</Typography>
                                </Box>
                                <Chip
                                  label={row.registrationStatus}
                                  size="small"
                                  sx={{ ...getStatusChipStyles(status), fontWeight: 600, flexShrink: 0 }}
                                />
                              </Box>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                                <Box>
                                  <Typography sx={{ color: '#657074', fontSize: '0.72rem', textTransform: 'uppercase' }}>Session</Typography>
                                  <Typography sx={{ fontSize: '0.9rem' }}>{row.session}</Typography>
                                </Box>
                                <Box>
                                  <Typography sx={{ color: '#657074', fontSize: '0.72rem', textTransform: 'uppercase' }}>Submitted</Typography>
                                  <Typography sx={{ fontSize: '0.9rem' }}>{row.createdAt || '-'}</Typography>
                                </Box>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                  <Typography sx={{ color: '#657074', fontSize: '0.72rem', textTransform: 'uppercase' }}>Job title</Typography>
                                  <Typography sx={{ fontSize: '0.9rem' }}>{row.jobTitle || '-'}</Typography>
                                </Box>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                  <Typography sx={{ color: '#657074', fontSize: '0.72rem', textTransform: 'uppercase' }}>Company</Typography>
                                  <Typography sx={{ fontSize: '0.9rem' }}>{row.company || '-'}</Typography>
                                </Box>
                              </Box>
                              {renderRegistrationAction(registrant, true)}
                            </Stack>
                          </Paper>
                        );
                      })
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      <Dialog
        open={Boolean(selectedRegistrant)}
        onClose={() => !registeringId && setSelectedRegistrant(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Register attendee</DialogTitle>
        <DialogContent>
          {selectedRegistrant && (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Alert severity="info">
                This will create the webinar registration and send the configured confirmation email.
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr)', gap: 1 }}>
                <Typography sx={{ color: '#657074' }}>Attendee</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedRegistrant.name}</Typography>
                <Typography sx={{ color: '#657074' }}>Email</Typography>
                <Typography sx={{ wordBreak: 'break-word' }}>{selectedRegistrant.email}</Typography>
                <Typography sx={{ color: '#657074' }}>Session</Typography>
                <Typography>{getSessionLabel(selectedRegistrant)}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setSelectedRegistrant(null)}
            disabled={Boolean(registeringId)}
            variant="outlined"
            sx={outlinedButtonStyles}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegisterRegistrant}
            disabled={Boolean(registeringId)}
            startIcon={registeringId ? <CircularProgress size={16} color="inherit" /> : <PersonAddAlt1Icon />}
            sx={buttonStyles}
          >
            {registeringId ? 'Registering...' : 'Register and send email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedRejectRegistrant)}
        onClose={() => !rejectingId && setSelectedRejectRegistrant(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Reject registration</DialogTitle>
        <DialogContent>
          {selectedRejectRegistrant && (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Alert severity="warning">
                This will move the record out of the registrations list and into the rejected tab.
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr)', gap: 1 }}>
                <Typography sx={{ color: '#657074' }}>Attendee</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedRejectRegistrant.name}</Typography>
                <Typography sx={{ color: '#657074' }}>Email</Typography>
                <Typography sx={{ wordBreak: 'break-word' }}>{selectedRejectRegistrant.email}</Typography>
                <Typography sx={{ color: '#657074' }}>Session</Typography>
                <Typography>{getSessionLabel(selectedRejectRegistrant)}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setSelectedRejectRegistrant(null)}
            disabled={Boolean(rejectingId)}
            variant="outlined"
            sx={outlinedButtonStyles}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectRegistrant}
            disabled={Boolean(rejectingId)}
            startIcon={rejectingId ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{
              ...buttonStyles,
              background: '#9a3030',
              '&:hover': { background: '#7d2626' },
            }}
          >
            {rejectingId ? 'Rejecting...' : 'Reject registration'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
